import { useMemo } from 'react';

const MONTH_LABELS = ['', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

/**
 * Groups individual-projection rows by consultor, splitting by year (current vs
 * previous). Returns a `{[consultorId]: {name, ly, cy}}` dict for downstream math.
 */
function groupProjectionsByConsultor(projIndiv, currentYear) {
  const byConsultor = {};
  for (const r of projIndiv) {
    const cid = r.ID_Cliente;
    if (!byConsultor[cid]) byConsultor[cid] = { name: r.NomeCompleto, ly: {}, cy: {} };
    const bucket = r.Ano === currentYear - 1 ? byConsultor[cid].ly : byConsultor[cid].cy;
    bucket[r.Mes] = r.Valor || 0;
  }
  return byConsultor;
}

/**
 * Derives per-consultant factor + running monthly totals used to build the
 * "Individual" projection chart series.
 */
function aggregateIndividualProjection(byConsultor, currentMonth) {
  const monthlyLY = Array.from({ length: 13 }, () => 0);
  const monthlyCY = Array.from({ length: 13 }, () => 0);
  const monthlyProj = Array.from({ length: 13 }, () => 0);
  let sumProj = 0, sumYTD = 0, sumLYSame = 0;

  for (const d of Object.values(byConsultor)) {
    const lySame = sumRange(d.ly, 1, currentMonth);
    const ytd = sumRange(d.cy, 1, currentMonth);
    let factor;
    if (lySame > 0) factor = ytd / lySame;
    else if (ytd > 0) factor = 2.0;
    else factor = 0;

    const remaining = Array.from({ length: 12 - currentMonth }, (_, i) =>
      (d.ly[currentMonth + 1 + i] || 0) * factor
    ).reduce((a, b) => a + b, 0);
    sumProj += ytd + remaining;
    sumYTD += ytd;
    sumLYSame += lySame;

    for (let m = 1; m <= 12; m++) {
      monthlyLY[m] += d.ly[m] || 0;
      if (m <= currentMonth) {
        monthlyCY[m] += d.cy[m] || 0;
        monthlyProj[m] += d.cy[m] || 0;
      } else {
        monthlyProj[m] += (d.ly[m] || 0) * factor;
      }
    }
  }

  return { sumProj, sumYTD, sumLYSame, monthlyLY, monthlyCY, monthlyProj };
}

function sumRange(obj, from, to) {
  let s = 0;
  for (let i = from; i <= to; i++) s += obj[i] || 0;
  return s;
}

function buildSimpleYearlyChart(compFat) {
  if (!compFat) return [];
  const out = [];
  for (let m = 1; m <= 12; m++) {
    const cur = (compFat.currentYear || []).find(c => c.Mes === m);
    const last = (compFat.lastYear || []).find(c => c.Mes === m);
    out.push({ mes: MONTH_LABELS[m], 'Ano Atual': cur ? cur.Valor : 0, 'Ano Anterior': last ? last.Valor : 0 });
  }
  return out;
}

function buildComissoesChart(comparativo) {
  if (!comparativo) return [];
  const out = [];
  for (let m = 1; m <= 12; m++) {
    const cur = (comparativo.currentYear || []).find(c => c.Mes === m);
    const last = (comparativo.lastYear || []).find(c => c.Mes === m);
    out.push({
      mes: MONTH_LABELS[m],
      'Ano Atual': cur ? cur.ValorComissaoAnoAtual : 0,
      'Ano Anterior': last ? last.ValorComissaoAnoAnterior : 0,
    });
  }
  return out;
}

function buildProjChart(compFat, networkGrowthFactor, currentMonth, currentYear, indiv) {
  const cy = compFat.currentYear || [];
  const ly = compFat.lastYear || [];
  const out = [];

  // Previous year cumulative
  let accumLY = 0;
  for (let m = 1; m <= 12; m++) {
    const lv = (ly.find(l => l.Mes === m) || {}).Valor || 0;
    accumLY += lv;
    out.push({ label: `${MONTH_LABELS[m]} ${currentYear - 1}`, idx: m, 'Ano Anterior': Math.round(accumLY) });
  }

  // Current year cumulative (real + projections)
  let accumCY = 0, accumProjRede = 0, accumProjIndiv = 0;
  for (let m = 1; m <= 12; m++) {
    const cv = (cy.find(c => c.Mes === m) || {}).Valor || 0;
    const lv = (ly.find(l => l.Mes === m) || {}).Valor || 0;
    const isActual = m <= currentMonth;
    if (isActual) {
      accumCY += cv;
      accumProjRede = accumCY;
      accumProjIndiv += indiv ? indiv.monthlyCY[m] : cv;
    } else {
      accumProjRede += lv * networkGrowthFactor;
      accumProjIndiv += indiv ? indiv.monthlyProj[m] : lv * networkGrowthFactor;
    }
    const entry = { label: `${MONTH_LABELS[m]} ${currentYear}`, idx: 12 + m };
    if (isActual) entry['Real'] = Math.round(accumCY);
    else {
      entry['Proj. Rede'] = Math.round(accumProjRede);
      entry['Proj. Individual'] = Math.round(accumProjIndiv);
    }
    if (m === currentMonth) {
      entry['Proj. Rede'] = Math.round(accumCY);
      entry['Proj. Individual'] = Math.round(accumProjIndiv);
    }
    out.push(entry);
  }
  return out;
}

/**
 * Aggregates the three dashboard charts + headline KPIs:
 * - chartData: commissions comparison (current vs last year)
 * - fatChartData: monthly billing comparison
 * - projChartData: cumulative billing with network + individual projections
 * - projectedTotal / projectedIndivTotal / growthPct / growthPctIndiv: KPIs
 */
export default function useDashboardProjection({ comparativo, compFat, compFatPessoal, projIndiv }) {
  return useMemo(() => {
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();

    const chartData = buildComissoesChart(comparativo);
    const fatChartData = buildSimpleYearlyChart(compFat);
    const fatPessoalChartData = buildSimpleYearlyChart(compFatPessoal);

    // ── Method 1: Network-as-a-block projection ──
    let actualYTD = 0, lastYearTotal = 0, networkGrowthFactor = 1, projectedTotal = 0, growthPct = 0;
    if (compFat) {
      const cy = compFat.currentYear || [];
      const ly = compFat.lastYear || [];
      actualYTD = cy.reduce((s, c) => s + (c.Valor || 0), 0);
      lastYearTotal = ly.reduce((s, l) => s + (l.Valor || 0), 0);
      const lastYearSamePeriod = ly.filter(l => l.Mes <= currentMonth).reduce((s, l) => s + (l.Valor || 0), 0);
      networkGrowthFactor = lastYearSamePeriod > 0 ? actualYTD / lastYearSamePeriod : 1;
      growthPct = lastYearSamePeriod > 0 ? ((networkGrowthFactor - 1) * 100) : 0;
      const remainingProjected = ly.filter(l => l.Mes > currentMonth).reduce((s, l) => s + ((l.Valor || 0) * networkGrowthFactor), 0);
      projectedTotal = actualYTD + remainingProjected;
    }

    // ── Method 2: Sum of individual consultant projections ──
    let projectedIndivTotal = 0, growthPctIndiv = 0, indiv = null;
    if (projIndiv.length > 0) {
      const byConsultor = groupProjectionsByConsultor(projIndiv, currentYear);
      indiv = aggregateIndividualProjection(byConsultor, currentMonth);
      projectedIndivTotal = indiv.sumProj;
      growthPctIndiv = indiv.sumLYSame > 0 ? ((indiv.sumYTD / indiv.sumLYSame - 1) * 100) : 0;
    }

    const projChartData = compFat ? buildProjChart(compFat, networkGrowthFactor, currentMonth, currentYear, indiv) : [];

    return {
      chartData, fatChartData, fatPessoalChartData, projChartData,
      projectedTotal, projectedIndivTotal,
      actualYTD, lastYearTotal,
      growthPct, growthPctIndiv,
      currentMonth, currentYear,
    };
  }, [comparativo, compFat, compFatPessoal, projIndiv]);
}
